import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Package, TrendingUp, ScanLine } from "lucide-react";

export default function RepairParts({ repairId, onTotalsChange }) {
  const [data, setData] = useState({ parts: [], totals: { revenue: 0, cost: 0, profit: 0 } });
  const [partsCatalog, setPartsCatalog] = useState([]);
  const [selectedPart, setSelectedPart] = useState("");
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [scanMode, setScanMode] = useState(false);
  const [scanInput, setScanInput] = useState("");

  const load = async () => {
    const r = await api.get(`/admin/repairs/${repairId}/parts`);
    setData(r.data);
    if (onTotalsChange) onTotalsChange(r.data.totals);
  };

  useEffect(() => {
    load();
    api.get("/admin/parts").then((r) => setPartsCatalog(r.data));
    // eslint-disable-next-line
  }, [repairId]);

  const addPart = async () => {
    if (!selectedPart) { toast.error("Alege o piesa"); return; }
    setAdding(true);
    try {
      await api.post(`/admin/repairs/${repairId}/parts`, { part_id: selectedPart, quantity: parseInt(qty) || 1 });
      toast.success("Piesa adaugata. Stocul a scazut automat.");
      setSelectedPart("");
      setQty(1);
      load();
      api.get("/admin/parts").then((r) => setPartsCatalog(r.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare");
    } finally { setAdding(false); }
  };

  const removeLine = async (lineId) => {
    if (!confirm("Scoate piesa? Stocul se restaureaza.")) return;
    await api.delete(`/admin/repairs/${repairId}/parts/${lineId}`);
    load();
    api.get("/admin/parts").then((r) => setPartsCatalog(r.data));
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!scanInput.trim()) return;
    setAdding(true);
    try {
      const r = await api.get(`/admin/parts/by-sku/${encodeURIComponent(scanInput.trim())}`);
      const part = r.data;
      if ((part.stock || 0) <= 0) {
        toast.error(`${part.name}: stoc 0!`);
      } else {
        await api.post(`/admin/repairs/${repairId}/parts`, { part_id: part.id, quantity: 1 });
        toast.success(`+ 1x ${part.name}`);
        load();
        api.get("/admin/parts").then((r) => setPartsCatalog(r.data));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cod inexistent");
    } finally {
      setScanInput("");
      setAdding(false);
    }
  };

  const availableParts = partsCatalog.filter((p) => (p.stock || 0) > 0);

  return (
    <div data-testid="repair-parts">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Package className="w-4 h-4 text-[#00E5FF]" />
        <div className="text-xs uppercase tracking-widest text-[#00E5FF] font-bold">Piese folosite ({data.parts.length})</div>
        {data.parts.length > 0 && (
          <div className="ml-auto flex items-center gap-3 text-xs">
            <span className="text-neutral-400">Pret: <span className="text-white font-medium">{data.totals.revenue.toFixed(0)} RON</span></span>
            <span className="text-neutral-400">Cost: <span className="text-white">{data.totals.cost.toFixed(0)} RON</span></span>
            <span className={`inline-flex items-center gap-1 font-display text-base ${data.totals.profit > 0 ? "text-green-400" : "text-neutral-400"}`}>
              <TrendingUp className="w-3 h-3" /> {data.totals.profit.toFixed(0)} RON
            </span>
          </div>
        )}
      </div>

      <div className="bg-neutral-900 border border-neutral-800 p-3 mb-3 flex flex-wrap items-end gap-2">
        <button
          type="button"
          onClick={() => setScanMode((v) => !v)}
          data-testid="toggle-scan-mode"
          className={`px-3 py-2 text-xs uppercase tracking-widest border ${scanMode ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "border-neutral-700 text-neutral-300 hover:border-[#00E5FF] hover:text-[#00E5FF]"}`}
        >
          <ScanLine className="w-3.5 h-3.5 inline mr-1" /> Scan
        </button>
        {scanMode ? (
          <form onSubmit={handleScan} className="flex-1 min-w-[200px] flex gap-2">
            <Input
              autoFocus
              value={scanInput}
              onChange={(e) => setScanInput(e.target.value)}
              placeholder="Scaneaza cu Zebra → Enter (adauga 1 buc automat)"
              className="bg-neutral-950 border-[#00E5FF] rounded-none h-10 font-mono"
              data-testid="scan-input-repair"
            />
            <Button type="submit" disabled={adding} className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none h-10">
              <Plus className="w-4 h-4" />
            </Button>
          </form>
        ) : (
          <>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Alege piesa din stoc</div>
              <Select value={selectedPart} onValueChange={setSelectedPart}>
                <SelectTrigger data-testid="select-part" className="bg-neutral-950 border-neutral-800 rounded-none h-10"><SelectValue placeholder={availableParts.length === 0 ? "Niciuna in stoc" : "Alege..."} /></SelectTrigger>
                <SelectContent className="bg-neutral-950 border-neutral-800 text-white max-h-72">
                  {availableParts.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.price} RON · stoc: {p.stock})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-20">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Cant.</div>
              <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-neutral-950 border-neutral-800 rounded-none h-10" />
            </div>
            <Button onClick={addPart} disabled={adding || !selectedPart} data-testid="add-part-to-repair-btn" className="bg-[#00E5FF] hover:bg-[#5CEEFF] text-black font-semibold uppercase tracking-wider rounded-none h-10">
              <Plus className="w-4 h-4 mr-1" /> Adauga
            </Button>
          </>
        )}
      </div>

      {data.parts.length === 0 ? (
        <div className="text-xs text-neutral-500 py-3 text-center">Nicio piesa adaugata pe aceasta reparatie</div>
      ) : (
        <div className="bg-neutral-900 border border-neutral-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-800">
                <th className="p-2">Piesa</th>
                <th className="p-2 w-16 text-center">Cant.</th>
                <th className="p-2 w-24 text-right">Pret/buc</th>
                <th className="p-2 w-24 text-right">Total</th>
                <th className="p-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {data.parts.map((p) => (
                <tr key={p.line_id} className="border-b border-neutral-900">
                  <td className="p-2">
                    <div className="text-sm">{p.name}</div>
                    {p.sku && <div className="text-[10px] text-neutral-500 font-mono">{p.sku}</div>}
                  </td>
                  <td className="p-2 text-center">{p.quantity}</td>
                  <td className="p-2 text-right text-neutral-300">{p.unit_price} RON</td>
                  <td className="p-2 text-right text-[#00E5FF] font-medium">{(p.quantity * p.unit_price).toFixed(0)} RON</td>
                  <td className="p-2 text-center">
                    <button onClick={() => removeLine(p.line_id)} className="text-neutral-500 hover:text-red-500">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
